import React, { useEffect, useState } from 'react';
import api from './api';
import ProjectModal from './ProjectModal';
import ProjectCard from './ProjectCard';
import { PROJECT_STATUS_BADGE_CLASS, PROJECT_STATUS_LABELS, PROJECT_PRIORITIES, PROJECT_PRIORITY_LABELS, PROJECT_PRIORITY_BADGE_CLASS, PROJECT_TYPES, PROJECT_TYPE_LABELS, PROJECT_TYPE_BADGE_CLASS, PROJECT_SERVICES, PROJECT_SERVICE_LABELS, libelleType, libelleService, projectCode, userDisplayName, formatDate } from './helpers';

// « Archivés » n'est pas un statut mais une vue à part : le serveur ne renvoie
// les projets archivés que si on les demande explicitement (?archived=1).
const FILTERS = ['Tous', 'Active', 'Delayed', 'Completed', 'Cancelled', 'Archived'];
const FILTER_LABELS = { Tous: 'Tous', Active: 'Actifs', Delayed: 'En retard', Completed: 'Terminés', Cancelled: 'Annulés', Archived: 'Archivés' };

function Projects({ onSelectProject, users, searchQuery }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('Tous');
  const [priorityFilter, setPriorityFilter] = useState('Toutes');
  // Deux axes de tri du portefeuille : à quoi sert le projet, et qui le pilote.
  // « Non classé » est une valeur utile en soi : elle montre ce qui reste à
  // ranger.
  const [typeFilter, setTypeFilter] = useState('Tous');
  const [serviceFilter, setServiceFilter] = useState('Tous');
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState('cards'); // 'cards' | 'summary'

  const load = React.useCallback(() => {
    setLoading(true);
    api
      .get(filter === 'Archived' ? '/projects?archived=1' : '/projects')
      .then((res) => setProjects(res.data))
      .catch(() => setError('Impossible de charger les projets.'))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const query = (searchQuery || '').trim().toLowerCase();

  let filtered =
    filter === 'Tous' || filter === 'Archived'
      ? projects
      : projects.filter((p) => p.status === filter);
  if (priorityFilter !== 'Toutes') {
    filtered = filtered.filter((p) => p.priority === priorityFilter);
  }
  if (typeFilter !== 'Tous') {
    filtered = filtered.filter((p) =>
      typeFilter === 'Aucun' ? !p.project_type : p.project_type === typeFilter
    );
  }
  if (serviceFilter !== 'Tous') {
    filtered = filtered.filter((p) =>
      serviceFilter === 'Aucun' ? !p.project_service : p.project_service === serviceFilter
    );
  }
  if (query) {
    filtered = filtered.filter((p) => p.name.toLowerCase().includes(query) || projectCode(p.id).toLowerCase().includes(query) || (p.description || '').toLowerCase().includes(query));
  }

  const handleSaved = (project) => {
    setShowModal(false);
    load();
    // Un projet neuf n'existe vraiment qu'une fois sa charte rédigée : on
    // emmène directement la personne sur le formulaire plutôt que de la
    // laisser deviner qu'il faut y aller.
    if (project?.id && onSelectProject) onSelectProject(project, 'charte');
  };

  // Compteurs affichés dans les listes déroulantes : on veut savoir combien de
  // projets se cachent derrière chaque choix avant de cliquer.
  const compter = (champ, valeur) =>
    projects.filter((p) => (valeur === 'Aucun' ? !p[champ] : p[champ] === valeur)).length;

  const managerName = (id) => userDisplayName(users.find((u) => u.id === id));
  const budgetPct = (p) => (p.budget_allocated ? Math.round(((p.budget_spent || 0) / p.budget_allocated) * 100) : 0);

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="fade-in">
      <div className="page-header-row">
        <div className="filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f}
              className={`filter-pill ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
        {/* Cinq contrôles tiennent mal sur une seule ligne : on les laisse
            passer à la ligne plutôt que de les voir se chevaucher. */}
        <div className="projets-outils">
          <div className="view-toggle">
            <button className={`view-toggle-btn ${view === 'cards' ? 'active' : ''}`} onClick={() => setView('cards')}>Cartes</button>
            <button className={`view-toggle-btn ${view === 'summary' ? 'active' : ''}`} onClick={() => setView('summary')}>Résumé</button>
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="btn btn-sm">
            <option value="Tous">Tous les types</option>
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>
                {PROJECT_TYPE_LABELS[t]} ({compter('project_type', t)})
              </option>
            ))}
            <option value="Aucun">Non classés ({compter('project_type', 'Aucun')})</option>
          </select>
          <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className="btn btn-sm">
            <option value="Tous">Tous les services</option>
            {PROJECT_SERVICES.map((sv) => (
              <option key={sv} value={sv}>
                {PROJECT_SERVICE_LABELS[sv]} ({compter('project_service', sv)})
              </option>
            ))}
            <option value="Aucun">Non affectés ({compter('project_service', 'Aucun')})</option>
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="btn btn-sm">
            <option value="Toutes">Toutes priorités</option>
            {PROJECT_PRIORITIES.map((p) => (
              <option key={p} value={p}>{PROJECT_PRIORITY_LABELS[p]}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Nouveau projet</button>
        </div>
      </div>

      {view === 'cards' && (
        <div className="projects-grid">
          {filtered.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              users={users}
              showPriority
              onSelect={onSelectProject}
            />
          ))}
          {filtered.length === 0 && (
            <div className="empty-state">
              {filter === 'Archived' ? 'Aucun projet archivé.' : 'Aucun projet dans cette catégorie.'}
              {filter !== 'Archived' && (
                <div className="empty-state-action">
                  <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Nouveau projet</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {view === 'summary' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Réf.</th>
                <th>Projet</th>
                <th>Type</th>
                <th>Service</th>
                <th>Statut</th>
                <th>Priorité</th>
                <th>Avancement</th>
                <th>Budget consommé</th>
                <th>Tâches</th>
                <th>Chef de projet</th>
                <th>Échéance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="data-table-row" onClick={() => onSelectProject(p)} style={{ cursor: 'pointer' }}>
                  <td>{projectCode(p.id)}</td>
                  <td><div style={{ fontWeight: 600 }}>{p.name}</div></td>
                  <td>
                    {p.project_type ? (
                      <span className={`badge ${PROJECT_TYPE_BADGE_CLASS[p.project_type] || 'badge-todo'}`}>
                        {libelleType(p.project_type)}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>
                  <td>{libelleService(p.project_service) || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td>
                    <span className={`badge ${PROJECT_STATUS_BADGE_CLASS[p.status] || 'badge-active'}`}>
                      {PROJECT_STATUS_LABELS[p.status] || p.status}
                    </span>
                  </td>
                  <td>
                    {p.priority && (
                      <span className={`badge ${PROJECT_PRIORITY_BADGE_CLASS[p.priority] || 'badge-todo'}`}>{p.priority}</span>
                    )}
                  </td>
                  <td style={{ minWidth: 110 }}>
                    <div className="progress-bar" style={{ margin: '0 0 4px 0' }}>
                      <div className="progress-fill" style={{ width: `${p.progress_percent || 0}%` }} />
                    </div>
                    <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{p.progress_percent || 0}%</span>
                  </td>
                  <td>{p.budget_allocated ? `${budgetPct(p)}%` : '-'}</td>
                  <td>
                    {p.task_done_count ?? 0}/{p.task_count ?? 0}
                    {p.milestone_count > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        ◆ {p.milestone_done_count}/{p.milestone_count} jalons
                      </div>
                    )}
                  </td>
                  <td>{managerName(p.project_manager_id)}</td>
                  <td>{formatDate(p.end_date)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="empty-state">Aucun projet ne correspond à ces filtres.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ProjectModal
          users={users}
          onClose={() => setShowModal(false)}
          onSaved={handleSaved}
          onDeleted={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

export default Projects;
